<template>
  <div class="pick-order-detail-container">
    <!-- 页面标题：显示当前拣货单号 -->
    <el-page-header @back="handleBack" :content="`拣货单详情 - ${currentPickOrderNo}`"></el-page-header>

    <!-- 加载状态 -->
    <el-loading
        v-if="loading"
        fullscreen
        text="正在查询拣货单详情..."
    ></el-loading>

    <!-- 错误提示：修正属性绑定语法 -->
    <el-alert
        v-else-if="errorMsg"
        type="error"
        :description="errorMsg"
        show-icon
        style="margin: 20px 0;"
    ></el-alert>

    <!-- 拣货单明细表格：prop与JSON字段完全对应 -->
    <el-table
        v-else-if="pickOrderList.length > 0"
        :data="pickOrderList"
        border
        stripe
        style="width: 100%; margin-top: 20px;"
        :header-cell-style="{ 'text-align': 'center' }"
        :cell-style="{ 'text-align': 'center' }"
    >
      <!-- 拣货单ID -->
      <el-table-column
          prop="pickOrderId"
          label="拣货单ID"
          width="120"
      ></el-table-column>
      <!-- 拣货单号 -->
      <el-table-column
          prop="pickOrderNo"
          label="拣货单号"
          width="200"
      ></el-table-column>
      <!-- 关联订单ID -->
      <el-table-column
          label="关联订单ID"
          width="120"
      >
        <template slot-scope="scope">
          {{orderMap.get(scope.row.ordersId) }}
        </template>
      </el-table-column>
      <!-- 上架单号（可能为null，显示“-”） -->
      <el-table-column
          prop="putawayNo"
          label="上架单号"
          width="160"
      >
        <template slot-scope="scope">
          {{ scope.row.putawayNo || '-' }}
        </template>
      </el-table-column>
      <!-- 篮子编号 -->
      <el-table-column
          prop="pickBasket"
          label="篮子编号"
          width="100"
      ></el-table-column>
      <!-- 库位编码 -->
      <el-table-column
          prop="locationCode"
          label="库位编码"
          width="120"
      ></el-table-column>
      <!-- 库位ID -->
      <el-table-column
          prop="locationId"
          label="库位ID"
          width="100"
      ></el-table-column>
      <!-- 商品SKU -->
      <el-table-column
          prop="productSku"
          label="商品SKU"
          width="120"
      ></el-table-column>
      <!-- 本次下架数量 -->
      <el-table-column
          prop="currentPickQuantity"
          label="下架数量"
          width="120"
      ></el-table-column>
      <!-- 创建人 -->
      <el-table-column
          prop="createBy"
          label="创建人"
          width="120"
      ></el-table-column>
      <!-- 创建时间 -->
      <el-table-column
          label="创建时间"
          width="220"
      >
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <!-- 更新时间 -->
      <el-table-column
          label="更新时间"
          width="220"
      >
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.updatedTime) }}
        </template>
      </el-table-column>
    </el-table>

    <!-- 无数据提示 -->
    <div v-else class="no-data">
      <i class="el-icon-empty"></i>
      <p>暂无该拣货单的明细数据</p>
    </div>
  </div>
</template>

<script>

import {getPickOrders} from "@/api/api";
import {computed} from "vue";
import {mapState} from "vuex";

export default {
  name: "PickOrderDetail",
  data() {
    return {
      loading: false,
      errorMsg: "",
      pickOrderList: [], // 存储JSON中的data数组
      currentPickOrderNo: "" // 存储当前查询的拣货单号（用于标题显示）
    };
  },
  mounted() {
    this.getPickOrderDetail();
  },
  watch: {
    // 监听路由参数变化，重新加载数据
    '$route.query.pickOrderNo': {
      handler(newVal) {
        if (newVal) {
          this.getPickOrderDetail();
        }
      },
      immediate: false
    }
  },
  methods: {
    /**
     * 调用接口查询拣货单详情（适配返回的JSON结构）
     */
    getPickOrderDetail() {
      // 1. 获取路由中的拣货单号
      const pickOrderNo = this.$route.query.pickOrderNo;
      if (!pickOrderNo || pickOrderNo.trim() === "") {
        this.errorMsg = "缺少拣货单号参数，无法查询详情！";
        return;
      }
      this.currentPickOrderNo = pickOrderNo.trim(); // 赋值给标题

      // 2. 发送查询请求
      this.loading = true;
      getPickOrders({ pickOrderNo: this.currentPickOrderNo })
          .then(response => {
            // 适配JSON结构：response.data 对应后端返回的 {msg, code, data}
            const res = response;
            if (res.code === 200) {
              // res.data 即为JSON中的明细数组（[{pickOrderId,...}, ...]）
              this.pickOrderList = res.data || [];
            } else {
              this.errorMsg = res.msg || "查询失败，请重试！";
              this.$message.error(this.errorMsg);
            }
          })
          .catch(error => {
            this.errorMsg = "网络异常，查询失败！";
            this.$message.error(this.errorMsg);
            console.error("查询异常：", error);
          })
          .finally(() => {
            this.loading = false;
          });
    },

    /**
     * 格式化日期时间：将 "2025-09-20T19:15:15" 转为 "2025-09-20 19:15:15"
     */
    formatDateTime(isoTime) {
      if (!isoTime) return "-";
      return isoTime.replace("T", " ").slice(0, 19);
    },

    /**
     * 返回上一页（拣货单列表页）
     */
    handleBack() {
      this.$router.go(-1);
      // 若需固定跳回列表页：this.$router.push("/pickOrder/list");
    }
  },
  computed: {
    ...mapState({
      orderMap: state => state.common.orderMap
    })
  }
};
</script>

<style scoped>
.pick-order-detail-container {
  padding: 20px;
  background-color: #fff;
  min-height: calc(100vh - 120px);
}

.no-data {
  text-align: center;
  padding: 80px 0;
  color: #999;
}

.no-data .el-icon-empty {
  font-size: 48px;
  margin-bottom: 16px;
}

.no-data p {
  font-size: 16px;
}
</style>